"use client";

import {Typography} from "@repo/ui/aops-components/typography";
import {cn} from "@repo/ui/lib/utils";
import {stegaClean} from "next-sanity";
import {DiscoveryCard as DiscoveryCardType} from "@repo/sanity-utils/schema/types";
import {DiscoveryButton} from "@/app/components/DiscoveryButton/DiscoveryButton";
import type {ResolvedSanityImage} from "@/lib/discoveryMedia";
import {SanityMediaFieldContent} from "@repo/sanity-utils/components/media/SanityMediaFieldContent";
import {AopsPortableText} from "../../PortableText/PortableTextComponents";
import styles from "./DiscoveryCardBlock.module.css";

interface DiscoveryCardBlockProps {
	discoveryCard: DiscoveryCardType;
	imageData?: ResolvedSanityImage;
	className?: string;
}

function getMediaSection({
	media,
	imageData,
	sizes,
}: {
	media: NonNullable<DiscoveryCardType["media"]>[number] | undefined;
	imageData?: ResolvedSanityImage;
	sizes: string;
}) {
	const image =
		media?._type === "image" && imageData
			? {
					src: imageData.url,
					alt: media.alt ?? "",
					width: imageData.width,
					height: imageData.height,
				}
			: null;
	const videoUrl = media?._type === "videoEmbed" ? media.url : null;

	if (!image && !videoUrl) {
		return null;
	}

	return (
		<div className={styles.mediaSection}>
			<SanityMediaFieldContent
				image={image}
				videoUrl={videoUrl}
				className={styles.mediaImage}
				sizes={sizes}
				fill
			/>
		</div>
	);
}

/**
 * Card used inside DiscoveryCardList. Layout is CSS-driven across breakpoints.
 * Plain divs (not Mantine Card.Section) so padding stays inside the card bounds —
 * Card.Section uses negative margins that bleed past the edge.
 */
export function DiscoveryCardBlock({
	discoveryCard,
	imageData,
	className,
}: DiscoveryCardBlockProps) {
	const mediaSection = getMediaSection({
		media: discoveryCard.media?.[0],
		imageData,
		// Stacked on mobile/desktop; side-by-side (~half width) only at tablet portrait.
		sizes:
			"(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw",
	});

	const buttons = Array.isArray(discoveryCard.buttons)
		? discoveryCard.buttons
		: [];

	return (
		<article className={cn(styles.card, className)}>
			<div className={styles.cardRow}>
				{mediaSection}
				<div className={styles.cardBody}>
					<Typography
						variant={stegaClean(discoveryCard.title?.displayType)}
						as={stegaClean(discoveryCard.title?.semanticLevel)}
						className={styles.title}
					>
						{discoveryCard.title?.text ?? ""}
					</Typography>
					<div className={styles.bodyText}>
						<AopsPortableText value={discoveryCard.bodyText ?? []} />
					</div>
				</div>
			</div>
			{buttons.length > 0 && (
				<div className={styles.buttonsSection}>
					<div className={styles.buttonsWrapper}>
						{buttons.map((button, index) => (
							<DiscoveryButton
								key={button._key ?? `${button.text}-${index}`}
								button={button}
							/>
						))}
					</div>
				</div>
			)}
		</article>
	);
}
